import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import Ico from './ui/Ico';

const NAVY = '#0B1220';
const BLUE = '#2563EB';
const TEAL = '#00D4CB';

function ScoreBadge({ score }) {
  if (score === undefined || score === null) return <span className="text-xs text-gray-400">—</span>;
  const cfg =
    score >= 80 ? { bg: 'bg-green-100 text-green-800 border-green-200', label: <><Ico e="🔥" />Quente</> } :
    score >= 60 ? { bg: 'bg-blue-100 text-blue-800 border-blue-200', label: <><Ico e="👍" />Bom</> } :
    score >= 40 ? { bg: 'bg-amber-100 text-amber-800 border-amber-200', label: <><Ico e="⚡" />Médio</> } :
                  { bg: 'bg-gray-100 text-gray-600 border-gray-200', label: <><Ico e="❄️" />Frio</> };
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-semibold ${cfg.bg}`}>
      {score} pts · {cfg.label}
    </span>
  );
}

function ScoreBar({ score, max = 100 }) {
  const pct = Math.min(100, Math.max(0, ((score || 0) / max) * 100));
  const color = score >= 80 ? 'from-green-400 to-emerald-500' : score >= 60 ? 'from-blue-400 to-indigo-500' : score >= 40 ? 'from-amber-400 to-orange-500' : 'from-gray-300 to-gray-400';
  return (
    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
      <div className={`h-full bg-gradient-to-r ${color} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 z-[99999] flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="font-bold text-gray-900 text-lg">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>,
    document.body
  );
}

const TRIGGER_FIELDS = [
  { value: 'email', label: 'Tem email' },
  { value: 'phone', label: 'Tem telefone' },
  { value: 'company', label: 'Tem empresa' },
  { value: 'status', label: 'Status' },
  { value: 'source', label: 'Origem' },
  { value: 'funnel_id', label: 'Está em funil' },
];

export default function LeadScoring({ api, embedded = false }) {
  const [leads, setLeads] = useState([]);
  const [scores, setScores] = useState({}); 
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [activeTab, setActiveTab] = useState('ranking');
  const [sortBy, setSortBy] = useState('score_desc');
  const emptyRule = { name: '', description: '', trigger_field: 'email', trigger_value: 'present', points: 10, active: true };
  const [ruleForm, setRuleForm] = useState(emptyRule);
  const [savingRule, setSavingRule] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [lRes, rRes] = await Promise.all([
        api.get('/leads?limit=200'),
        api.get('/scoring-rules'),
      ]);
      const raw = lRes.data;
      const leadsList = Array.isArray(raw?.leads) ? raw.leads : Array.isArray(raw) ? raw : [];
      setLeads(leadsList);
      const rawRules = rRes.data;
      setRules(Array.isArray(rawRules) ? rawRules : []);

      // Fetch scores for all leads in parallel (batch of 20)
      const scoreMap = {};
      const batch = leadsList.slice(0, 50);
      await Promise.all(batch.map(async l => {
        try {
          const sr = await api.get(`/leads/${l.id}/score`);
          if (sr.data) scoreMap[l.id] = sr.data.total_score ?? 0;
        } catch { scoreMap[l.id] = l.score || 0; }
      }));
      setScores(scoreMap);
    } catch { toast.error('Erro ao carregar dados'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const recalculateAll = async () => {
    setRecalculating(true);
    try {
      let done = 0;
      for (const lead of leads.slice(0, 100)) {
        try {
          const r = await api.post(`/leads/${lead.id}/score/recalculate`);
          if (r.data) {
            setScores(prev => ({ ...prev, [lead.id]: r.data.total_score ?? 0 }));
          }
          done++;
        } catch {}
      }
      toast.success(`${done} leads recalculados!`);
    } catch { toast.error('Erro ao recalcular scores'); }
    finally { setRecalculating(false); }
  };

  const saveRule = async () => {
    if (!ruleForm.name) return toast.error('Nome obrigatório');
    setSavingRule(true);
    try {
      if (editingRule) {
        await api.put(`/scoring-rules/${editingRule.id}`, ruleForm);
        toast.success('Regra atualizada!');
      } else {
        await api.post('/scoring-rules', ruleForm);
        toast.success('Regra criada!');
      }
      setShowRuleForm(false);
      const rRes = await api.get('/scoring-rules');
      setRules(rRes.data || []);
    } catch { toast.error('Erro ao salvar regra'); }
    finally { setSavingRule(false); }
  };

  const deleteRule = async (id) => {
    if (!window.confirm('Remover esta regra?')) return;
    try {
      await api.delete(`/scoring-rules/${id}`);
      setRules(r => r.filter(x => x.id !== id));
      toast.success('Regra removida');
    } catch { toast.error('Erro ao remover regra'); }
  };

  const openCreate = () => { setEditingRule(null); setRuleForm(emptyRule); setShowRuleForm(true); };
  const openEdit = (r) => { setEditingRule(r); setRuleForm({ name: r.name, description: r.description || '', trigger_field: r.trigger_field, trigger_value: r.trigger_value, points: r.points, active: r.active }); setShowRuleForm(true); };

  // Sort leads for ranking
  const scoredLeads = leads.map(l => ({ ...l, computed_score: scores[l.id] ?? l.score ?? 0 }));
  const sorted = [...scoredLeads].sort((a, b) => {
    if (sortBy === 'score_desc') return b.computed_score - a.computed_score;
    if (sortBy === 'score_asc') return a.computed_score - b.computed_score;
    if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
    return 0;
  });

  // Stats
  const hot = scoredLeads.filter(l => l.computed_score >= 80).length;
  const good = scoredLeads.filter(l => l.computed_score >= 60 && l.computed_score < 80).length;
  const medium = scoredLeads.filter(l => l.computed_score >= 40 && l.computed_score < 60).length;
  const cold = scoredLeads.filter(l => l.computed_score < 40).length;
  const avgScore = scoredLeads.length > 0 ? Math.round(scoredLeads.reduce((s, l) => s + l.computed_score, 0) / scoredLeads.length) : 0;

  return (
    <div className="space-y-6">
      {/* Header — only when standalone */}
      {!embedded && (
        <div className="rounded-2xl px-4 sm:px-7 py-5 flex flex-col gap-4 sm:flex-row sm:items-center" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${BLUE} 70%, ${TEAL} 100%)` }}>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center text-3xl shrink-0"><Ico e="🎯" bare /></div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-white">Lead Scoring</h1>
              <p className="text-white/70 text-sm mt-0.5">Qualifique seus leads automaticamente com regras inteligentes</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 sm:ml-auto">
            <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20" onClick={recalculateAll} disabled={recalculating}>
              {recalculating ? <><Ico e="⏳" />Recalculando…</> : <><Ico e="🔄" />Recalcular Todos</>}
            </Button>
            <Button className="bg-white text-blue-700 hover:bg-blue-50" onClick={openCreate}>+ Nova Regra</Button>
          </div>
        </div>
      )}

      {/* Embedded action bar */}
      {embedded && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-gray-600 text-sm font-medium">Qualifique seus leads automaticamente com regras inteligentes</p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="text-sm" onClick={recalculateAll} disabled={recalculating}>
              {recalculating ? <><Ico e="⏳" />Recalculando…</> : <><Ico e="🔄" />Recalcular Todos</>}
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white text-sm" onClick={openCreate}>+ Nova Regra</Button>
          </div>
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <div className="bg-white rounded-xl border border-gray-100 px-4 py-3 text-center shadow-sm">
          <p className="text-2xl font-bold text-gray-800">{avgScore}</p>
          <p className="text-xs text-gray-500 mt-0.5">Score médio</p>
        </div>
        <div className="bg-white rounded-xl border border-green-200 px-4 py-3 text-center shadow-sm">
          <p className="text-2xl font-bold text-green-700">{hot}</p>
          <p className="text-xs text-green-600 mt-0.5"><Ico e="🔥" />Quentes (≥80)</p>
        </div>
        <div className="bg-white rounded-xl border border-blue-200 px-4 py-3 text-center shadow-sm">
          <p className="text-2xl font-bold text-blue-700">{good}</p>
          <p className="text-xs text-blue-600 mt-0.5"><Ico e="👍" />Bons (60–79)</p>
        </div>
        <div className="bg-white rounded-xl border border-amber-200 px-4 py-3 text-center shadow-sm">
          <p className="text-2xl font-bold text-amber-700">{medium}</p>
          <p className="text-xs text-amber-600 mt-0.5"><Ico e="⚡" />Médios (40–59)</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 text-center shadow-sm">
          <p className="text-2xl font-bold text-gray-500">{cold}</p>
          <p className="text-xs text-gray-400 mt-0.5"><Ico e="❄️" />Frios (&lt;40)</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {[{ id: 'ranking', label: <><Ico e="🏆" />Ranking de Leads</> }, { id: 'rules', label: <><Ico e="⚙️" />Regras de Scoring</> }].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all ${activeTab === tab.id ? 'bg-white shadow-sm text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Ranking Tab */}
      {activeTab === 'ranking' && (
        <Card className="border border-gray-200">
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
            <p className="font-semibold text-gray-800">Leads ({leads.length})</p>
            <select className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="score_desc">Score ↓ (maior primeiro)</option>
              <option value="score_asc">Score ↑ (menor primeiro)</option>
              <option value="name">Nome A-Z</option>
            </select>
          </div>
          <CardContent className="p-0">
            {loading ? <p className="text-center py-12 text-gray-400">Carregando…</p> : sorted.length === 0 ? (
              <p className="text-center py-12 text-gray-400">Nenhum lead encontrado</p>
            ) : (
              <div className="divide-y divide-gray-50">
                {sorted.slice(0, 100).map((lead, idx) => (
                  <div key={lead.id} className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50">
                    <span className="text-sm font-bold text-gray-400 w-6 text-center">{idx + 1}</span>
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-teal-400 flex items-center justify-center text-white font-bold text-sm shrink-0">
                      {(lead.name || '?')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate">{lead.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {lead.email && <span className="text-xs text-gray-400 truncate">{lead.email}</span>}
                        {lead.company && <span className="text-xs text-gray-500">· {lead.company}</span>}
                      </div>
                    </div>
                    <div className="w-32 shrink-0">
                      <ScoreBar score={lead.computed_score} />
                    </div>
                    <div className="shrink-0">
                      <ScoreBadge score={lead.computed_score} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Rules Tab */}
      {activeTab === 'rules' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500">{rules.length} regras configuradas. Cada regra adiciona ou remove pontos do lead quando a condição é satisfeita.</p>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={openCreate}>+ Nova Regra</Button>
          </div>

          {rules.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <div className="text-5xl mb-3"><Ico e="⚙️" bare /></div>
              <p className="font-medium text-gray-600">Nenhuma regra configurada</p>
              <p className="text-sm mt-1">Crie regras para pontuar seus leads automaticamente</p>
              <Button className="mt-4 bg-blue-600 hover:bg-blue-700 text-white" onClick={openCreate}>Criar primeira regra</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {rules.map(rule => (
                <Card key={rule.id} className={`border ${rule.active ? 'border-blue-200' : 'border-gray-200 opacity-60'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 text-sm">{rule.name}</p>
                        {rule.description && <p className="text-xs text-gray-400 mt-0.5">{rule.description}</p>}
                      </div>
                      <span className={`text-lg font-bold ml-2 ${rule.points > 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {rule.points > 0 ? '+' : ''}{rule.points}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 bg-gray-50 rounded-lg px-2 py-1.5 mb-3">
                      <span className="font-medium">Campo:</span> {TRIGGER_FIELDS.find(f => f.value === rule.trigger_field)?.label || rule.trigger_field}
                      {rule.trigger_value !== 'present' && <span> = "{rule.trigger_value}"</span>}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => openEdit(rule)}><Ico e="✏️" />Editar</Button>
                      <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600 text-xs" onClick={() => deleteRule(rule.id)}><Ico e="🗑️" bare /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Rule Form Modal */}
      <Modal open={showRuleForm} onClose={() => setShowRuleForm(false)} title={editingRule ? 'Editar Regra' : 'Nova Regra de Scoring'}>
        <div className="space-y-4">
          <div>
            <Label>Nome da regra *</Label>
            <Input className="mt-1" placeholder="Ex: Lead com telefone" value={ruleForm.name} onChange={e => setRuleForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <Label>Descrição</Label>
            <Input className="mt-1" placeholder="Quando esta regra se aplica…" value={ruleForm.description} onChange={e => setRuleForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Campo que aciona</Label>
              <select className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={ruleForm.trigger_field} onChange={e => setRuleForm(f => ({ ...f, trigger_field: e.target.value }))}>
                {TRIGGER_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Valor esperado</Label>
              <Input className="mt-1" placeholder="present (qualquer valor)" value={ruleForm.trigger_value} onChange={e => setRuleForm(f => ({ ...f, trigger_value: e.target.value }))} />
              <p className="text-xs text-gray-400 mt-0.5">Use "present" para "campo preenchido"</p>
            </div>
          </div>
          <div>
            <Label>Pontos {ruleForm.points >= 0 ? '(positivos = recompensa)' : '(negativos = penalidade)'}</Label>
            <Input type="number" className="mt-1" placeholder="Ex: 10 ou -5" value={ruleForm.points} onChange={e => setRuleForm(f => ({ ...f, points: Number(e.target.value) }))} />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="rule-active" checked={ruleForm.active} onChange={e => setRuleForm(f => ({ ...f, active: e.target.checked }))} className="rounded" />
            <label htmlFor="rule-active" className="text-sm font-medium text-gray-700">Regra ativa</label>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="outline" onClick={() => setShowRuleForm(false)}>Cancelar</Button>
            <Button onClick={saveRule} disabled={savingRule} className="bg-blue-600 hover:bg-blue-700 text-white">{savingRule ? 'Salvando…' : 'Salvar Regra'}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
